import React, { useMemo } from "react";
import classNames from "classnames";
import { Card, CardBody } from "reactstrap";
import { useTranslation } from "react-i18next";
import { formatDateTimeForDisplay } from "utils/dateUtils";
import { extractCreditBureauFromCustomer } from "utils/normalizeCreditBureauApi";
import "./CustomerCreditBureau.css";

const formatCreditBureauDate = (value) => {
  if (!value) return "—";
  const formatted = formatDateTimeForDisplay(value);
  return formatted ? formatted.replace(/-/g, "/") : "—";
};

const formatCreditBureauValue = (value) => {
  if (value == null || value === "") return "—";
  if (typeof value === "object") {
    return (
      value.id ??
      value.user_id ??
      value.code ??
      value.name ??
      value.full_name ??
      "—"
    );
  }
  return String(value);
};

const formatCreditBureauScore = (value) => {
  if (value == null || value === "") return "—";
  return String(value);
};

export const normalizeCreditBureauList = (
  value,
  customerDetail = null,
  mergedCustomerDetail = null
) => {
  const fromValue = extractCreditBureauFromCustomer(
    value != null ? { credit_bureau: value } : null
  );
  if (fromValue.length) return fromValue;

  const fromDetail = extractCreditBureauFromCustomer(customerDetail);
  if (fromDetail.length) return fromDetail;

  return extractCreditBureauFromCustomer(mergedCustomerDetail);
};

const getCreditBureauSortTime = (entry) => {
  const raw = entry?.requested_date ?? entry?.request_date;
  if (!raw) return 0;
  const time = new Date(raw).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const CreditBureauCard = ({ creditBureau, cardClassName }) => {
  const { t } = useTranslation();

  const rows = useMemo(() => {
    if (!creditBureau) return [];
    return [
      {
        label: t("customerDetail.creditBureauSection.requestedDate"),
        value: formatCreditBureauDate(
          creditBureau.requested_date ?? creditBureau.request_date
        ),
      },
      {
        label: t("customerDetail.status"),
        value: formatCreditBureauValue(creditBureau.status),
      },
      {
        label: t("customerDetail.creditBureauSection.requestedBy"),
        value: formatCreditBureauValue(
          creditBureau.requested_by ??
            creditBureau.requested_by_id ??
            creditBureau.requested_user
        ),
      },
      {
        label: t("customerDetail.creditBureauSection.equifax"),
        value: formatCreditBureauScore(
          creditBureau.equifax ?? creditBureau.equifax_score
        ),
      },
      {
        label: t("customerDetail.creditBureauSection.transunion"),
        value: formatCreditBureauScore(
          creditBureau.transunion ?? creditBureau.transunion_score
        ),
      },
      {
        label: t("customerDetail.creditBureauSection.experian"),
        value: formatCreditBureauScore(
          creditBureau.experian ?? creditBureau.experian_score
        ),
      },
    ];
  }, [creditBureau, t]);

  const statusInitial = useMemo(() => {
    const status = creditBureau?.status;
    if (!status) return "—";
    return String(status).trim().charAt(0).toUpperCase() || "—";
  }, [creditBureau?.status]);

  return (
    <Card className={classNames("mt-0", cardClassName)}>
      <CardBody className="customer-credit-bureau-card__body">
        <div className="customer-credit-bureau-card__layout">
          <div
            className="customer-credit-bureau-card__status-badge"
            aria-hidden
          >
            {statusInitial}
          </div>
          <div className="customer-credit-bureau-card__rows">
            {rows.map((row) => (
              <div className="customer-credit-bureau-card__row" key={row.label}>
                <span className="customer-credit-bureau-card__label">
                  {row.label}
                </span>
                <span className="customer-credit-bureau-card__value">
                  {row.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

const CustomerCreditBureau = ({
  creditBureau,
  customerDetail = null,
  mergedCustomerDetail = null,
  isLoading = false,
  isTheme2 = false,
  isLightTheme = false,
}) => {
  const { t } = useTranslation();

  const entries = useMemo(() => {
    return normalizeCreditBureauList(
      creditBureau,
      customerDetail,
      mergedCustomerDetail
    ).sort((a, b) => getCreditBureauSortTime(b) - getCreditBureauSortTime(a));
  }, [creditBureau, customerDetail, mergedCustomerDetail]);

  const cardClassName = classNames("customer-credit-bureau-card", {
    "customer-credit-bureau-card--theme2": isTheme2,
    "customer-credit-bureau-card--theme2-light": isTheme2 && isLightTheme,
    "customer-credit-bureau-card--theme2-dark": isTheme2 && !isLightTheme,
  });

  if (isLoading) {
    return (
      <Card className="mt-0">
        <CardBody>
          <div>{t("customerDetail.loadingCustomerDetails")}</div>
        </CardBody>
      </Card>
    );
  }

  if (!entries.length) {
    return (
      <Card className="mt-0">
        <CardBody>
          <div>{t("customerDetail.noCreditBureauFound")}</div>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="customer-credit-bureau-list">
      {entries.map((entry, index) => (
        <CreditBureauCard
          key={`${entry.requested_date ?? entry.request_date ?? "credit"}-${index}`}
          creditBureau={entry}
          cardClassName={cardClassName}
        />
      ))}
    </div>
  );
};

export default CustomerCreditBureau;
